import { AppInterface } from "@/interfaces/app.interface";
import { AppPackageInterface } from "@/interfaces/appPackage.interface";
import { BackendAppInterface } from "@/interfaces/backendApp.interface";
// @ts-ignore
import vueI18n from "@/libs/i18n";
import AppUtils from "@/utils/apps";
// @ts-ignore
import iAutomotive from "@saysimple-plugins/1i-automotive";
// @ts-ignore
import AdyenApp from "@saysimple-plugins/adyen";
// @ts-ignore
import AutolineApp from "@saysimple-plugins/autoline";
// @ts-ignore
import EmailApp from "@saysimple-plugins/email";
// Import the SaysimpleApp from all available plugins
// @ts-ignore
import ExampleApp from "@saysimple-plugins/example";
// @ts-ignore
import FfnApp from "@saysimple-plugins/ffn";
// @ts-ignore
import IdasApp from "@saysimple-plugins/idas";
// @ts-ignore
import LefApp from "@saysimple-plugins/lef";
// @ts-ignore
import PlanItApp from "@saysimple-plugins/plan-it";
// @ts-ignore
import Journeys from "@saysimple-plugins/journeys";
// @ts-ignore
import Flows from "@saysimple-plugins/flows";
// @ts-ignore
import RealworksApp from "@saysimple-plugins/realworks";
// @ts-ignore
import UntappdApp from "@saysimple-plugins/untappd";
// @ts-ignore
import WebsolveApp from "@saysimple-plugins/websolve";
// @ts-ignore
import AcceptEasyApp from "@saysimple-plugins/accept-easy";
// @ts-ignore
import NpsApp from "@saysimple-plugins/nps";
// @ts-ignore
import EdulanceApp from "@saysimple-plugins/edulance";
// @ts-ignore
import HubspotApp from "@saysimple-plugins/hubspot";
// @ts-ignore
import ConniptionBubbleApp from "@saysimple-plugins/conniption-bubble";
import { computed, Ref, ref } from "@vue/composition-api";
// @ts-ignore
import zapierWorkflow from "@saysimple-plugins/zapier-workflow";
// @ts-ignore
import C4cSapApp from "@saysimple-plugins/c4c-sap";

import GammaApp from "@saysimple-plugins/gamma";

import YellowYardApp from "@saysimple-plugins/yellow-yard";
import ToastInterface from "vue-toastification/dist/types/src/ts/interface";
import { AppDataEnabledFeatureNames, AppDataEnabledFeatures } from "@/types/appDataEnabledFeatures.type";
import axios from "@/libs/axios";
import { AxiosResponse } from "axios";

const apps: Ref<AppInterface[]> = ref([]);
const isLoaded: Ref<boolean> = ref<boolean>(false);

export const availableApps: Record<string, AppPackageInterface> = {};

function makeAvailable(app: AppPackageInterface) {
    if (availableApps[app.plugin.name]) {
        throw Error(`App ${app.plugin.name} already available`);
    }
    availableApps[app.plugin.name] = app;
}

// Make all plugins available.
// This loads the data from each plugin NPM package.
(
    [
        ExampleApp,
        UntappdApp,
        RealworksApp,
        IdasApp,
        FfnApp,
        PlanItApp,
        Journeys,
        Flows,
        AdyenApp,
        LefApp,
        AutolineApp,
        EmailApp,
        WebsolveApp,
        iAutomotive,
        AcceptEasyApp,
        NpsApp,
        ConniptionBubbleApp,
        EdulanceApp,
        HubspotApp,
        zapierWorkflow,
        C4cSapApp,
        GammaApp,
        YellowYardApp,
    ] as const
).forEach((app) => makeAvailable(app));

// Transform PluginSetting[] to a key-value pair
function processSettings(pluginSettings: Array<{ name: string; value: unknown }> = []) {
    const settings: Record<string, unknown> = {};
    pluginSettings.forEach((setting) => (settings[setting.name] = setting.value));

    return settings;
}

export function useApps() {
    // @TODO: Get apps when empty?
    const isLoading: Ref<boolean> = ref(false);

    // @ts-ignore
    const register = (
        newApp: BackendAppInterface,
        setIsLoaded = true,
        emit?: (event: string, ...args: unknown[]) => void,
        toast?: ReturnType<typeof ToastInterface>
    ): void => {
        if (setIsLoaded && !isLoaded.value) {
            isLoaded.value = true;
        }
        // Allow both array or single entry to be registered
        if (Array.isArray(newApp)) {
            newApp.forEach((app) => register(app, setIsLoaded, emit, toast));
            return;
        }

        // Find matching package from available apps
        // @TODO: fix as AppPackageInterface
        const appPackage: AppPackageInterface = availableApps[newApp.packageName] ?? ({} as AppPackageInterface);

        // See if app is already registered
        const registeredApp = apps.value.find((app) => app.packageName === newApp.packageName);

        // When app is already registered, we only update the settings
        if (registeredApp) {
            registeredApp.settings = processSettings(newApp.settings);
            return;
        }

        // Combine data from app and package
        // @ts-ignore
        const app: AppInterface = {
            ...newApp,
            settings: processSettings(newApp.settings),
            package: appPackage.plugin,
            components: {
                ...appPackage.components,
            },
        };

        // Add AppUtils separately, as we need to give it itself as parameter
        app.utils = AppUtils(app);
        if (emit && toast) {
            app.utils.setEmitAndToast(emit, toast);
        }

        if (appPackage.locales) {
            Object.entries(appPackage.locales).forEach(([lang, messages]) => {
                vueI18n.mergeLocaleMessage(lang, messages);
            });
        }

        // @ts-ignore
        apps.value.push(app);
    };

    const enabledFeatures = computed(() =>
        apps.value
            .filter((app) => app.settings.active)
            .flatMap((app) => app.package?.saysimple?.enabledFeatures ?? [])
            .filter((feature) => feature.enabled)
    );

    const getEnabledFeature = (featureName: AppDataEnabledFeatureNames): AppDataEnabledFeatures | undefined => {
        if (!isLoaded.value && !isLoading.value) {
            // Load the apps if they are not registered yet
            loadApps();
        }

        // @ts-ignore
        return enabledFeatures.value.find((feature) => feature.name === featureName);
    };
    const loadApps = () => {
        if (isLoaded.value) {
            return;
        }

        isLoading.value = true;

        axios
            .get("/apps", {
                params: {
                    fields: "name,enabled,packageName",
                },
            })
            .then((result: AxiosResponse<BackendAppInterface>) => result.data)
            .then((apps: BackendAppInterface) => register(apps))
            .catch(() => {});

        isLoading.value = false;
    };

    return {
        register,
        getEnabledFeature,
        loadApps,
        apps,
        isLoaded,
    };
}
